/**
 * About Page - REFACTORED with Unified Content System
 * This is the new version using useEditableContent hook
 */

import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { FaTrash } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import ConfirmModal from '../components/ConfirmModal';
import EditableModal from '../components/EditableModal';
import RichTextEditor from '../components/RichTextEditor';
import SEO from '../components/SEO';
import useEditableContent from '../hooks/useEditableContent';

const About = () => {
  const { isAdmin } = useAuth();

  // Use unified hook for each content type
  const {
    items: skills,
    loading: skillsLoading,
    showModal: showSkillModal,
    isCreating: isCreatingSkill,
    formData: skillForm,
    updateFormData: updateSkillForm,
    confirmOpen: confirmOpenSkill,
    itemToDelete: skillToDelete,
    startCreate: startCreateSkill,
    startEdit: startEditSkill,
    cancelEdit: cancelSkillEdit,
    save: saveSkill,
    deleteItem: deleteSkill,
    confirmDelete: confirmDeleteSkill,
    cancelDelete: cancelDeleteSkill
  } = useEditableContent('skill', { page: 'about', section: 'skills' });

  const {
    items: paragraphs,
    loading: paragraphsLoading,
    showModal: showParaModal,
    isCreating: isCreatingPara,
    formData: paraForm,
    updateFormData: updateParaForm,
    confirmOpen: confirmOpenPara,
    itemToDelete: paraToDelete,
    startCreate: startCreatePara,
    startEdit: startEditPara,
    cancelEdit: cancelParaEdit,
    save: savePara,
    deleteItem: deletePara,
    confirmDelete: confirmDeletePara,
    cancelDelete: cancelDeletePara
  } = useEditableContent('paragraph', { page: 'about', section: 'bio' });

  const {
    items: aboutHeaders,
    showModal: showHeaderModal,
    formData: headerForm,
    updateFormData: updateHeaderForm,
    startEdit: startEditHeader,
    cancelEdit: cancelHeaderEdit,
    save: saveHeader
  } = useEditableContent('about-header', { page: 'about', section: 'header' });

  // Get the first header (there should only be one)
  const aboutHeader = aboutHeaders[0] || { title: '', subtitle: '' };

  return (
    <>
      <SEO
        title="About"
        description="Learn about Richard Plotka - Professor, Innovator & Technology Leader. Explore background, skills, and professional experience."
        keywords={['Richard Plotka', 'About', 'Professor', 'Background', 'Skills', 'Experience']}
      />
      <AboutContainer>
        <Container>
        <HeroSection
          as={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <HeroContent>
            <HeroHeader>
              <div>
                <Title>{aboutHeader.title || 'About Me'}</Title>
                <Subtitle>{aboutHeader.subtitle || 'Software Architect & Technology Leader'}</Subtitle>
              </div>
              {isAdmin && (
                <SecondaryButton onClick={() => startEditHeader(aboutHeader)}>
                  Edit
                </SecondaryButton>
              )}
            </HeroHeader>
          </HeroContent>
          <ProfileImageContainer
            as={motion.div}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ProfileImage src="/RMP-Headshot25.jpg" alt="Richard Plotka" />
          </ProfileImageContainer>
        </HeroSection>

        <ContentGrid>
          <BioSection
            as={motion.div}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <BioHeader>
              <SectionTitle>whoami</SectionTitle>
              {isAdmin && (
                <SecondaryButton onClick={() => startCreatePara({
                  metadata: { page: 'about', section: 'bio', order: paragraphs.length }
                })}>
                  Add Paragraph
                </SecondaryButton>
              )}
            </BioHeader>
            {paragraphsLoading ? (
              <p>Loading...</p>
            ) : (
              paragraphs.slice(0, 3).map((paragraph) => (
                <ParagraphCard key={paragraph._id}>
                  {isAdmin && (
                    <CardActions>
                      <EditIconButton onClick={() => startEditPara(paragraph)}>
                        <FiEdit2 />
                      </EditIconButton>
                      <DeleteIconButton onClick={() => confirmDeletePara(paragraph)}>
                        <FaTrash />
                      </DeleteIconButton>
                    </CardActions>
                  )}
                  <BioText dangerouslySetInnerHTML={{ __html: paragraph.description }} />
                </ParagraphCard>
              ))
            )}
          </BioSection>

          <SkillsSection
            as={motion.div}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <SkillsHeader>
              <SectionTitle>Skills & Expertise</SectionTitle>
              {isAdmin && (
                <SecondaryButton onClick={() => startCreateSkill({
                  data: { level: 90 },
                  metadata: { page: 'about', section: 'skills', order: skills.length }
                })}>
                  Add Skill
                </SecondaryButton>
              )}
            </SkillsHeader>
            <SkillsGrid>
              {skillsLoading ? (
                <p>Loading...</p>
              ) : (
                skills.map((skill) => (
                  <SkillItem key={skill._id}>
                    {isAdmin && (
                      <CardActions>
                        <EditIconButton onClick={() => startEditSkill(skill)}>
                          <FiEdit2 />
                        </EditIconButton>
                        <DeleteIconButton onClick={() => confirmDeleteSkill(skill)}>
                          <FaTrash />
                        </DeleteIconButton>
                      </CardActions>
                    )}
                    <SkillHeader>
                      <SkillName>{skill.title}</SkillName>
                      <SkillLevel>{skill.data?.level || 0}%</SkillLevel>
                    </SkillHeader>
                    <SkillBar>
                      <SkillProgress
                        level={skill.data?.level || 0}
                        as={motion.div}
                        initial={{ width: 0 }}
                        animate={{ width: `${skill.data?.level || 0}%` }}
                        transition={{ duration: 1, delay: 0.5 }}
                      />
                    </SkillBar>
                  </SkillItem>
                ))
              )}
            </SkillsGrid>
          </SkillsSection>
        </ContentGrid>

        {paragraphs.length > 3 && (
          <FullWidthBioSection
            as={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {paragraphs.slice(3).map((paragraph) => (
              <ParagraphCard key={paragraph._id}>
                {isAdmin && (
                  <CardActions>
                    <EditIconButton onClick={() => startEditPara(paragraph)}>
                      <FiEdit2 />
                    </EditIconButton>
                    <DeleteIconButton onClick={() => confirmDeletePara(paragraph)}>
                      <FaTrash />
                    </DeleteIconButton>
                  </CardActions>
                )}
                <BioText dangerouslySetInnerHTML={{ __html: paragraph.description }} />
              </ParagraphCard>
            ))}
          </FullWidthBioSection>
        )}

        {/* Header Edit Modal */}
        <EditableModal
          isOpen={showHeaderModal}
          isCreating={false}
          onClose={cancelHeaderEdit}
          onSave={saveHeader}
          title="Edit About Header"
        >
          <label>Title</label>
          <input
            type="text"
            value={headerForm.title || ''}
            onChange={e => updateHeaderForm({ title: e.target.value })}
            placeholder="About Me"
          />
          
          <label>Subtitle</label>
          <input
            type="text"
            value={headerForm.subtitle || ''}
            onChange={e => updateHeaderForm({ subtitle: e.target.value })}
            placeholder="Software Architect & Technology Leader"
          />
        </EditableModal>

        {/* Paragraph Edit Modal */}
        <EditableModal
          isOpen={showParaModal}
          isCreating={isCreatingPara}
          onClose={cancelParaEdit}
          onSave={savePara}
          title={isCreatingPara ? 'Add Paragraph' : 'Edit Paragraph'}
          maxWidth="800px"
        >
          <label>Paragraph Text</label>
          <RichTextEditor
            value={paraForm.description || ''}
            onChange={(content) => updateParaForm({ description: content })}
            placeholder="Enter paragraph content..."
            height="300px"
          />
        </EditableModal>

        {/* Skill Edit Modal */}
        <EditableModal
          isOpen={showSkillModal}
          isCreating={isCreatingSkill}
          onClose={cancelSkillEdit}
          onSave={saveSkill}
          title={isCreatingSkill ? 'Add Skill' : 'Edit Skill'}
        >
          <label>Skill Name</label>
          <input
            type="text"
            value={skillForm.title || ''}
            onChange={e => updateSkillForm({ title: e.target.value })}
            placeholder="e.g., React Development"
          />
          
          <label>Skill Level (%)</label>
          <input
            type="number"
            min="0"
            max="100"
            value={skillForm.data?.level || 90}
            onChange={e => updateSkillForm({ 
              data: { ...skillForm.data, level: parseInt(e.target.value) || 0 }
            })}
          />
          <LevelSlider
            type="range"
            min="0"
            max="100"
            value={skillForm.data?.level || 90}
            onChange={e => updateSkillForm({ 
              data: { ...skillForm.data, level: parseInt(e.target.value) }
            })}
          />
        </EditableModal>

        {/* Confirmation Modals */}
        <ConfirmModal
          isOpen={confirmOpenPara}
          title="Delete Paragraph"
          message="Are you sure you want to delete this paragraph? This action cannot be undone."
          onConfirm={() => deletePara(paraToDelete)}
          onClose={cancelDeletePara}
        />

        <ConfirmModal
          isOpen={confirmOpenSkill}
          title="Delete Skill"
          message="Are you sure you want to delete this skill? This action cannot be undone."
          onConfirm={() => deleteSkill(skillToDelete)}
          onClose={cancelDeleteSkill}
        />
      </Container>
    </AboutContainer>
    </>
  );
};

// Styled Components (keeping existing styles)
const AboutContainer = styled.div`
  min-height: 100vh;
  padding: 5rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 3rem;

  @media (max-width: 968px) {
    flex-direction: column;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  text-align: left;

  @media (max-width: 968px) {
    text-align: center;
  }
`;

const HeroHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ProfileImageContainer = styled.div`
  position: relative;
  width: 300px;
  height: 300px;
  flex-shrink: 0;

  @media (max-width: 968px) {
    width: 250px;
    height: 250px;
  }

  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: 10px;
    bottom: 10px;
    background: var(--accent-gradient);
    border-radius: 50%;
    z-index: -1;
  }
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: var(--text-secondary);
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 5rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const BioSection = styled.div``;

const FullWidthBioSection = styled.div`
  margin-bottom: 5rem;
`;

const BioHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ParagraphCard = styled.div`
  position: relative;
  padding: 0.25rem 2.5rem 0.25rem 0;
  margin-bottom: 0.25rem;
`;

const BioText = styled.p`
  line-height: 1.8;
  color: var(--text-secondary);
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: var(--text-primary);
`;

const SecondaryButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: transparent;
  color: var(--accent-primary);
  border: 2px solid var(--accent-primary);
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background: var(--accent-primary);
    color: white;
  }
`;

const SkillsSection = styled.div``;

const SkillsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const SkillsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SkillItem = styled.div`
  position: relative;
  padding: 1rem;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--accent-gradient);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: var(--accent);

    &::before {
      transform: scaleX(1);
    }
  }
`;

const CardActions = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 10;

  ${SkillItem}:hover &,
  ${ParagraphCard}:hover & {
    opacity: 1;
  }
`;

const EditIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid var(--accent-primary);
  color: var(--accent-primary);
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--accent-primary);
    color: white;
    transform: scale(1.05);
  }
`;

const DeleteIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  color: #ef4444;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #ef4444;
    color: white;
    transform: scale(1.05);
  }
`;

const SkillHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const SkillName = styled.span`
  font-weight: 600;
  color: var(--text-primary);
`;

const SkillLevel = styled.span`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const SkillBar = styled.div`
  height: 8px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  overflow: hidden;
`;

const SkillProgress = styled.div`
  height: 100%;
  background: var(--accent-gradient);
  border-radius: 4px;
  width: ${({ level }) => level}%;
`;

const LevelSlider = styled.input`
  width: 100%;
  margin-top: 0.5rem;
`;

export default About;
